import { useCheckEmailMutation } from "@/api/auth";
import { api, auth } from "@/api/utils";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { FieldInfo } from "@/components/FieldInfo";
import { Button } from "@/components/ui/Button";
import { IconButtonLink } from "@/components/ui/Link";
import { GivenwellIcon } from "@/icons/givenwell";
import { getErrorMessage } from "@/utils/error";
import { captureCustomEvent } from "@/utils/posthog";
import { config } from "@common/utils/config";
import { Box, Field, Flex, Form, Heading, Input, InputProps, Label, Span, Text } from "@givenwell/components";
import { ChevronLeftIcon, InvisibleIcon, VisibleIcon, WarningCircleIcon } from "@givenwell/core-solid-icons";
import { LoginBody } from "@givenwell/marketplace-api";
import { css, styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { unstable_PasswordToggleField as PasswordToggleField } from "radix-ui";
import { ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/sign-in")({
  component: Page,
  validateSearch: object({
    from: fallback(optional(string()), undefined),
    to: fallback(optional(string()), undefined),
  }),
});

type ErrorCode =
  | "invalid_email_or_password"
  | "invalid_request"
  | "locked_out"
  | "expired_refresh_token"
  | "authorized_not_registered"
  | "unknown";

function getErrorCode(error: Error) {
  const message = getErrorMessage(error);

  if (message === "Invalid email or password.") {
    return "invalid_email_or_password";
  }
  if (message === "Invalid request.") {
    return "invalid_request";
  }
  if (message === "Account locked out.") {
    return "locked_out";
  }
  if (message === "Refresh token expired.") {
    return "expired_refresh_token";
  }
  if (message === "authorized_not_registered") {
    return "authorized_not_registered";
  }

  return "unknown";
}

const route = getRouteApi("/sign-in");

type Step = "email" | "password" | "invite_sent";

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

function Page() {
  const { from, to } = route.useSearch();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("email");

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const inviteSentButtonRef = useRef<HTMLButtonElement>(null);

  const checkEmailMutation = useCheckEmailMutation();

  const mutation = useMutation({
    mutationFn: async (body: LoginBody) => {
      const result = await api.auth.jsonLogin({ application: "marketplace", requestBody: body });

      if (result.redirect_uri) {
        window.location.href = result.redirect_uri;
        return;
      }

      // TODO: awful dumb hack
      //
      // When the "application not registered" error happens, I successfully get
      // an access token, but no refresh token. So that's how I detect it.
      if (!result.refresh_token) {
        throw new Error("authorized_not_registered");
      }
      captureCustomEvent("user_logged_in", {});

      auth.saveAuthTokens({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        expires_in: 60 * 60,
      });
      navigate({
        to: to?.includes("sign-in") ? "/" : to || "/",
        replace: true,
      });
    },
    onError(error) {
      console.error(error);
    },
  });

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    } satisfies LoginBody,
    onSubmit: ({ value }) => {
      mutation.mutate(value);
    },
  });

  const handleFormSubmit = () => {
    if (step === "email") {
      checkEmailMutation.mutate(
        { email_address: form.getFieldValue("email") },
        {
          onSuccess: result => {
            setStep(result.status === "account_found" ? "password" : "invite_sent");
          },
        },
      );
    } else {
      form.handleSubmit();
    }
  };

  const id = useId();

  useLayoutEffect(() => {
    document.body.style.backgroundColor = colors.cream;
    return () => {
      document.body.style.removeProperty("background-color");
    };
  }, []);

  useEffect(() => {
    if (step === "password") {
      passwordInputRef.current?.focus();
    } else if (step === "invite_sent") {
      inviteSentButtonRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
  }, [step]);

  const goBackToEmailStep = () => {
    mutation.reset();
    checkEmailMutation.reset();
    setStep("email");
  };

  const stepError = step === "email" ? checkEmailMutation.error : mutation.error;
  const stepPending = step === "email" ? checkEmailMutation.isPending : mutation.isPending;

  return (
    <Box
      css={{
        py: 16,
        display: "flex",
        minHeight: "100%",
        flexDirection: "column",
        backgroundColor: colors.cream,
        "@sm": { backgroundColor: "transparent" },
      }}
    >
      <Box css={{ flex: "1 0 0px", display: "none", "@sm": { display: "block" } }} />
      <Flex css={{ justify: "center", overflow: "hidden", pb: 32 + 16 }}>
        <Box
          css={{
            width: "100%",
            maxWidth: 600,
            mx: "auto",
            backgroundColor: colors.cream,
            position: "relative",
            borderRadius: 24,
            pt: 32,
            pb: 12,
            "@sm": {
              // boxShadow: "0px 16px 32px hsl(0deg 0% 0% / 0.05)",
            },
          }}
        >
          <Box
            css={{
              width: "100%",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 30,
              py: 30,
              px: 20,
              "@sm": {
                maxWidth: 400,
              },
            }}
          >
            <Box css={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box css={{ position: "relative" }}>
                <GivenwellIcon css={{ height: 40 }} />
                <EnvironmentBadge
                  environmentName={config.environmentName}
                  isDev={import.meta.env.DEV}
                  position={{ left: -3, top: -25 }}
                />
              </Box>
              <Heading as="h1" css={{ fontScale: "3xl", weight: 300, color: colors.navy, fontFamily: fonts.ftHabit }}>
                Log in to Givenwell
              </Heading>
            </Box>
            {step === "email" && from && (
              <IconButtonLink aria-label="Back" variant="plain" color="neutral" to={from}>
                <ChevronLeftIcon />
              </IconButtonLink>
            )}

            <Form onSubmit={handleFormSubmit} id={id} css={{ spaceY: 30 }}>
              {stepError ? <ErrorAlert error={stepError} /> : null}

              <form.Field name="email">
                {field => (
                  <Box css={step === "email" ? undefined : visuallyHidden} aria-hidden={step !== "email"}>
                    <Field>
                      <Label>Email</Label>
                      <Input
                        ref={emailInputRef}
                        size="lg"
                        required={step === "email"}
                        type="email"
                        autoComplete="email"
                        placeholder="name@email.com"
                        value={field.state.value}
                        tabIndex={step === "email" ? undefined : -1}
                        onValueChange={field.handleChange}
                        css={{ background: colors.white }}
                      />
                      <FieldInfo field={field} />
                    </Field>
                  </Box>
                )}
              </form.Field>

              {(step === "password" || step === "invite_sent") && (
                <Flex
                  css={{
                    justify: "space-between",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    pl: 16,
                    pr: 0,
                    borderRadius: 8,
                    backgroundColor: colors.white,
                    border: `1px solid ${colors.outline}`,
                  }}
                >
                  <Box css={{ overflow: "hidden", flex: "1 1 auto", minWidth: 0 }}>
                    <Text css={{ fontScale: "xs", color: colors.text2 }}>Email</Text>
                    <Text
                      css={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {form.getFieldValue("email")}
                    </Text>
                  </Box>
                  <Button
                    ref={step === "invite_sent" ? inviteSentButtonRef : undefined}
                    type="button"
                    variant="plain"
                    color="primary"
                    size="sm"
                    onClick={goBackToEmailStep}
                    css={{ flexShrink: 0 }}
                  >
                    Change
                  </Button>
                </Flex>
              )}

              <form.Field name="password">
                {field => (
                  <Box css={step === "password" ? undefined : visuallyHidden} aria-hidden={step !== "password"}>
                    <Field>
                      <Label>
                        <Flex css={{ justify: "space-between", align: "center" }}>
                          Password
                          <Link to="/forgot-password" tabIndex={step === "password" ? undefined : -1}>
                            <Span
                              css={{
                                color: colors.blue500,
                                weight: 500,
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              Forgot Password?
                            </Span>
                          </Link>
                        </Flex>
                      </Label>
                      <PasswordInput
                        ref={passwordInputRef}
                        required={step === "password"}
                        size="lg"
                        autoComplete="current-password"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        tabIndex={step === "password" ? undefined : -1}
                      />
                      {/* <Input
                        ref={passwordInputRef}
                        required={step === "password"}
                        size="lg"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        css={{ background: colors.white }}
                        tabIndex={step === "email" ? -1 : undefined}
                      /> */}
                      <FieldInfo field={field} />
                    </Field>
                  </Box>
                )}
              </form.Field>

              {step === "invite_sent" && (
                <Box
                  role="status"
                  aria-live="polite"
                  css={{
                    spaceY: 8,
                    borderRadius: 16,
                    padding: 16,
                    backgroundColor: colors.cyanAccent,
                  }}
                >
                  <Heading css={{ fontScale: "lg", color: colors.text }}>Check your email</Heading>
                  <Text css={{ textWrap: "pretty" }}>
                    If you have been invited to Givenwell, we will send you an email with a link to set you up.
                  </Text>
                  <Text css={{ fontScale: "sm", color: colors.text2, textWrap: "pretty" }}>
                    Be sure to check your spam folder if it doesn’t arrive soon. Still stuck? Email our friendly team at{" "}
                    <A href="mailto:support@givenwell.com">support@givenwell.com</A>
                  </Text>
                </Box>
              )}

              {step !== "invite_sent" && (
                <Button
                  type="submit"
                  variant="filled"
                  color="pink"
                  loading={stepPending}
                  form={id}
                  size="lg"
                  css={{ width: "100%" }}
                >
                  {step === "email" ? "Continue" : "Sign in"}
                </Button>
              )}

              <Box css={{ fontScale: "xs", align: "center", color: colors.text2 }}>
                If you’re an Employer or Supplier wanting to access your dashboard, please{" "}
                <a href="https://manage.givenwell.com/">
                  <Span
                    css={{
                      color: colors.navy,
                      weight: 500,
                      textDecoration: "underline",
                    }}
                  >
                    log in here
                  </Span>
                </a>
                .
              </Box>
            </Form>
          </Box>
        </Box>
      </Flex>
      <Box css={{ flex: "1 0 0px" }} />
    </Box>
  );
}
function StillStuck() {
  return <Text css={{ textAlign: "center", fontScale: "xs", color: colors.text2 }}></Text>;
}

const A = styled("a", {
  color: colors.blue600,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": { textDecoration: "hover" },
});

export function PasswordInput(props: InputProps) {
  return (
    <PasswordToggleField.Root>
      <Box css={{ position: "relative" }}>
        <PasswordToggleField.Input asChild autoComplete="current-password">
          <Input css={{ input: { background: colors.white, paddingRight: 48 } }} {...props} />
        </PasswordToggleField.Input>
        <PasswordToggleField.Toggle className={passwordToggleButton()}>
          <PasswordToggleField.Icon visible={<VisibleIcon />} hidden={<InvisibleIcon />} />
        </PasswordToggleField.Toggle>
      </Box>
    </PasswordToggleField.Root>
  );
}
const passwordToggleButton = css({
  height: 48,
  width: 48,
  position: "absolute",
  top: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

function getAlertMessage(errorCode: ErrorCode): ReactNode {
  switch (errorCode) {
    case "invalid_email_or_password":
    case "authorized_not_registered":
      return (
        <>
          Invalid email or password. Did you mean to{" "}
          <a href="https://manage.givenwell.com/">
            <Span
              css={{
                color: colors.red600,
                weight: 700,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              sign in as Supplier or Employer?
            </Span>
          </a>
        </>
      );
    case "invalid_request":
      return "Invalid request.";
    case "locked_out":
      return "Account locked out.";
    case "expired_refresh_token":
      return "Refresh token expired.";
    case "unknown":
    default:
      return (
        <>
          An unknown error occurred. Please try again and{" "}
          <a href="mailto:support@givenwell.com" style={{ textDecoration: "underline" }}>
            contact support
          </a>{" "}
          if this continues
        </>
      );
  }
}

function ErrorAlert({ error }: { error: Error }) {
  const errorCode = getErrorCode(error);

  return (
    <Flex css={{ borderRadius: 16, padding: 16, backgroundColor: colors.red100, color: colors.red600, gap: 12 }}>
      <WarningCircleIcon style={{ flexShrink: "0" }} />
      <Text>{getAlertMessage(errorCode)}</Text>
    </Flex>
  );
}
